/**
 * LandingPageV3 — TREATX-LANDING-V3 PHASE B
 * -----------------------------------------
 * A clean, patient-first public presentation. Not a V2 enhancement: no trust
 * scores, no "most chosen" rankings, no booking-popularity or outcome claims.
 *
 * Canonical sources only (this page READS; it changes no journey):
 *   • treatments      → GET /public/catalog/active-provider-treatments
 *                       (provider-eligible inventory; never the master catalog)
 *   • providers       → GET /search/providers        (neutral, surface-aware)
 *   • announcements   → GET /public/landing-content   (Landing CMS)
 *   • certifications  → <CorporateCertificates /> (Corporate Certificates SSOT)
 *   • provider copy   → GET /public/site-content/provider_benefits_{hero,rows,cta}
 *                       (canonical Provider Benefits authority — same reader
 *                        the /provider-benefits page uses)
 *   • jurisdiction    → useSurface / useSurfaceRule  (GLOBAL vs TR)
 *   • legal footer    → <LegalFooter /> (legal CMS)
 *
 * Unpublished by design: `/` keeps rendering V1 until the owner marks V3
 * release-ready and publishes it from Website Content → Homepage Version Control.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, BadgeCheck, Building2, CalendarDays, ChevronRight, FileText,
  GraduationCap, MapPin, MessageSquare, Search, Share2, ShieldCheck, Smartphone,
  Users,
} from 'lucide-react';
import api from '../../utils/api';
import { useSurface, useSurfaceRule } from '../../context/SurfaceContext';
import CorporateCertificates from '../../components/CorporateCertificates';
import SEO from '../../components/SEO';
import { useHomepageSeoProps } from '../homepageSeo';
import { corporateIdentityAPI } from '../../utils/api';
// Landing CMS stores localized strings as {en, tr, de, ...}. `getLocalizedField`
// is the canonical app-wide resolver (active language -> English -> first
// non-empty -> ''), already used by V1 and the subscription UI. V3 had a private
// copy of the same logic; it now shares the one authority.
import { getLocalizedField as loc } from '../../context/LanguageContext';
// V1V3-PARITY-03 — the SAME authorities V1 uses to resume a journey.
import { usePatientFlow } from '../../context/PatientFlowContext';
import { useAuth } from '../../context/AuthContext';
import { useRBAC } from '../../context/RBACContext';
import { resolveDashboardRouteForActiveWorkspace } from '../../utils/dashboardRouteResolver';
// Visual refinement for V3 only — every rule is scoped to `.v3-root`.
import './landing-v3.css';
import { resolveIcon } from '../../lib/v3Icons';
import { compareSections } from '../../lib/v3SectionOrder';

// Card grids stay balanced whatever the owner adds: 4 across for 4 or 8 items,
// 3 across for 3, 5, 6 or 9. Classes are written out so Tailwind keeps them.
const GRID_COLUMNS = {
  1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4',
};
const gridColumns = (count) => GRID_COLUMNS[count] || (count % 4 === 0
  ? 'lg:grid-cols-4' : 'lg:grid-cols-3');

const Section = ({ id, className = '', children, testid }) => (
  <section id={id} data-testid={testid}
           className={`w-full px-5 sm:px-8 lg:px-12 ${className}`}>
    <div className="mx-auto w-full max-w-[1200px]">{children}</div>
  </section>
);

// `v3-eyebrow` / `v3-h2` are styling hooks for landing-v3.css. Same elements,
// same text, same i18n keys — presentation only.
const Eyebrow = ({ children }) => (  <p className="v3-eyebrow text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">{children}</p>
);

const H2 = ({ children }) => (
  <h2 className="v3-h2 mt-2 text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">{children}</h2>
);

const Lede = ({ children }) => (
  <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-slate-600">{children}</p>
);

const LandingPageV3 = ({ draftPreview = false, previewSurface = null }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language?.split('-')[0] || 'en';
  const navigate = useNavigate();
  const surfaceCtx = useSurface() || {};
  const surface = previewSurface || surfaceCtx.surface || 'GLOBAL';
  const campaignsAllowed = useSurfaceRule('campaigns');

  // V1V3-PARITY-03 — resume an in-progress journey. V1 offers this and V3 did
  // not, so a returning patient who landed on V3 had no route back into an
  // unfinished journey. Same canonical authorities as V1: the patient flow
  // owns progress, RBAC owns which dashboard the workspace resolves to. No
  // parallel state and no new storage. Suppressed in draft preview, where the
  // reviewer is a Super Admin inspecting content, not a patient mid-journey.
  const { flowState, getNextStep } = usePatientFlow();
  const { user } = useAuth();
  const { activeWorkspace, availableWorkspaces, activeWorkspaceId } = useRBAC();
  const hasActiveFlow = !!(flowState?.selectedTreatment || flowState?.intakeCompleted
    || (flowState?.clinicsSelected || []).length > 0);
  const isPatient = user?.role === 'patient' || user?.user_type === 'patient';
  const showContinueBanner = !draftPreview && (hasActiveFlow || isPatient);
  const flowStepLabel = () => {
    if (flowState?.paymentCompleted) return t('v3.resume.dashboard', 'View your dashboard');
    if (flowState?.selectedOffer) return t('v3.resume.payment', 'Complete your payment');
    if ((flowState?.clinicsSelected || []).length > 0) return t('v3.resume.offers', 'Review your offers');
    if (flowState?.intakeCompleted) return t('v3.resume.selectClinics', 'Select clinics for offers');
    if (flowState?.selectedTreatment) return t('v3.resume.profile', 'Complete your health profile');
    return t('v3.resume.browse', 'Browse treatments');
  };

  const [treatments, setTreatments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selected, setSelected] = useState('');
  const [certificates, setCertificates] = useState([]);
  // Landing CMS content scoped to THIS homepage version and surface.
  const [cms, setCms] = useState({ sections: {}, hero: null, features: {} });

  // V1V3-SSOT-02 — provider copy comes from the canonical Provider Benefits
  // authority (`/public/site-content/*`), the SAME one the Provider Benefits
  // page reads.
  //
  // Proven defect this replaces: these three keys were fetched through
  // `useContent`, which calls the Legal Content Engine
  // (`/public/content/resolve`). That engine does not hold them — all three
  // returned HTTP 404 (verified live) — so the CMS branch was dead and V3
  // silently fell back to its hardcoded defaults. The field names were wrong
  // too: the blocks carry `headline` / `body_md` / `cta_text`, never
  // `title` / `subtitle` / `primary_label`.
  // The surface is never sent; the server resolves GLOBAL/TR itself.
  const [providerContent, setProviderContent] = useState({ hero: null, rows: [], cta: null });

  useEffect(() => {
    let alive = true;
    const safe = (p) => p.then((r) => r.data).catch(() => null);
    Promise.all([
      safe(api.get('/public/catalog/active-provider-treatments')),
      safe(api.get('/search/providers?limit=6')),
      // Landing CMS — the SAME authority V1 uses. The surface is NOT sent: the
      // server resolves GLOBAL/TR canonically and ignores any client hint, so
      // the jurisdiction cannot be selected from the browser.
      // In `draftPreview` the Super Admin review route asks the SAME authority
      // through its authenticated admin reader, which additionally admits
      // variants that are still DRAFT. The surface is then an explicit
      // administrative choice, and an anonymous visitor never reaches it.
      draftPreview
        ? api.get(`/admin/landing-content/effective-preview?surface=${surface}`
                  + '&include_drafts=true&full=true')
          .then((r) => r.data?.content || null).catch(() => null)
        : safe(api.get('/public/landing-content?homepage_version=V3')),
      // canonical Corporate Certificates SSOT — the same call V1 uses
      corporateIdentityAPI.listCertificates({ surface: 'homepage' })
        .then((r) => r?.data?.certificates || [])
        .catch(() => []),
      // Canonical Provider Benefits authority — same reader the
      // /provider-benefits page uses. Shape: content.payload.blocks.
      safe(api.get('/public/site-content/provider_benefits_hero')),
      safe(api.get('/public/site-content/provider_benefits_rows')),
      safe(api.get('/public/site-content/provider_benefits_cta')),
    ]).then(([tr, pr, lc, certs, pbHero, pbRows, pbCta]) => {
      if (!alive) return;
      const firstActive = (res) => ((res?.content?.payload?.blocks) || [])
        .find((b) => b && b.active !== false) || null;
      setProviderContent({
        hero: firstActive(pbHero),
        cta: firstActive(pbCta),
        // Canonical ordering and visibility, identical to the Provider
        // Benefits page: hidden rows never reach the UI.
        rows: ((pbRows?.content?.payload?.blocks) || [])
          .filter((b) => b && b.active !== false)
          .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)),
      });
      setTreatments(Object.values(
        (tr?.treatments || [])
          .filter((x) => x?.slug && x?.name)
          // the catalog can expose the same treatment name under several
          // provider-specific slugs; show each name once.
          .reduce((acc, x) => ({ [String(x.name).toLowerCase()]: x, ...acc }), {}),
      ));
      setProviders((pr?.providers || []).slice(0, 6));
      // Publication status, scheduling, locale and surface applicability are all
      // decided by the CMS authority — this page applies no keyword heuristics.
      setAnnouncements(lc?.announcements || []);
      const sections = {};
      (lc?.section_copy || []).forEach((b) => {
        if (b?.section_key) sections[b.section_key] = b;
      });
      const features = {};
      (lc?.feature_content || []).forEach((b) => {
        const key = b?.section_key || 'other';
        features[key] = [...(features[key] || []), b];
      });
      setCms({
        sections,
        // The version-scoped reader already excludes unscoped legacy records,
        // so a V1 hero can never arrive here; preferring an explicitly
        // V3-scoped record keeps that true even if a record is mis-scoped.
        hero: (lc?.hero_content || []).find(
          (h) => (h?.homepage_versions || []).includes('V3'),
        ) || (lc?.hero_content || [])[0] || null,
        features,
      });
      setCertificates(Array.isArray(certs) ? certs : []);
    });
    return () => { alive = false; };
  }, [surface, draftPreview]);

  // CMS helpers: managed copy wins, the built-in default is the fallback, and a
  // section whose CMS record is switched off is not rendered at all.
  const sectionCopy = (key) => cms.sections[key] || null;
  const sectionVisible = (key) => (sectionCopy(key)?.active ?? true) !== false;
  const sectionTitle = (key, fallback) => loc(sectionCopy(key)?.title, lang) || fallback;
  const sectionBody = (key, fallback) => loc(sectionCopy(key)?.description, lang) || fallback;
  // TREATX-V3-CMS-GAP-01 — the remaining editorial fields of a managed section:
  // eyebrow, badge/chip label, small print, in-card link label and the two
  // call-to-action labels. Each falls back to the built-in default, so an
  // unmanaged section still renders exactly as it does today.
  const sectionField = (key, field, fallback = '') =>
    loc(sectionCopy(key)?.[field], lang) || fallback;
  const sectionHref = (key, field, fallback) =>
    (sectionCopy(key)?.[field] || '').trim() || fallback;
  const featureBlocks = (key) => cms.features[key] || [];
  // Managed child items of a section (How it works steps, Why TreatX points,
  // Founding programme lines). Scene records are visuals, never list items.
  const sectionItems = (key) => featureBlocks(key)
    .filter((b) => !b?.scene_key && b?.active !== false)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  // Dual-surface product visuals. The server already filtered to the approved
  // variant whose embedded language matches the resolved surface, so exactly
  // one variant per scene can arrive here; scenes are ordered by the shared
  // display order and then by scene identity, so GLOBAL and TR always show the
  // same scenes in the same sequence.
  const sceneVisuals = (key) => featureBlocks(key)
    .filter((b) => b?.scene_key && (b.compressed_url || b.image_url))
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
      || String(a.scene_key).localeCompare(String(b.scene_key)));
  const heroVisual = sceneVisuals('hero_visual')[0] || null;

  const visibleAnnouncements = useMemo(() => (announcements || [])
    .map((a) => ({
      key: a.block_id,
      title: loc(a.title, lang),
      description: loc(a.description, lang),
      button_text: loc(a.button_text, lang),
      button_link: typeof a.button_link === 'string' ? a.button_link : '',
    }))
    .filter((a) => a.title)
    .slice(0, 3), [announcements, lang]);

  // V1V3-PARITY-02 — provider capability cards.
  //
  // Proven defect this replaces: the cards used to read
  // `rows[i].title || rows[i].label` by raw array index. The canonical
  // `provider_benefits_rows` block carries NO `title` and NO `label` — its
  // fields are `headline`, `body_md`, `sort_order`, `active` (verified against
  // the live payload) — so the CMS branch could never produce a value and the
  // hardcoded fallback always won. The section looked CMS-managed and was not.
  //
  // Canonical selection now matches the Provider Benefits page exactly:
  // hidden rows are excluded, `sort_order` decides sequence, and the row's own
  // `headline` is the copy. Icons remain local and decorative — they are not
  // content, so they are assigned by slot and never imply row identity.
  const providerFeatureCards = useMemo(() => {
    const icons = [Users, Building2, MessageSquare, FileText, CalendarDays, BadgeCheck];
    const fallbacks = [
      t('v3.provider.f1', 'Your own customers'),
      t('v3.provider.f2', 'Lead and sales management'),
      t('v3.provider.f3', 'Patient messaging'),
      t('v3.provider.f4', 'Quotations and revisions'),
      t('v3.provider.f5', 'Operational follow-up'),
      t('v3.provider.f6', 'Management visibility'),
    ];
    const rows = providerContent.rows || [];
    return icons.map((icon, i) => ({
      icon,
      label: loc(rows[i]?.headline, lang) || fallbacks[i],
    }));
  }, [providerContent, lang, t]);

  const steps = useMemo(() => {
    const icons = [Search, Building2, MessageSquare, FileText];
    const fallback = [
      { title: t('v3.steps.discover.title', 'Discover treatments'),
        body: t('v3.steps.discover.body', 'Browse treatments that healthcare providers on TreatX actually offer today.') },
      { title: t('v3.steps.compare.title', 'Explore providers'),
        body: t('v3.steps.compare.body', 'Review provider details and the credentials TreatX has verified, side by side.') },
      { title: t('v3.steps.talk.title', 'Communicate directly'),
        body: t('v3.steps.talk.body', 'Message providers yourself. Your contact details stay under your control.') },
      { title: t('v3.steps.offer.title', 'Request a personal offer'),
        body: t('v3.steps.offer.body', 'Receive a written, itemised quotation prepared for your own case.') },
    ];
    // TREATX-V3-CMS-GAP-01 — the four cards are managed as child records of the
    // `how_it_works` section. Icons stay local and decorative: they are not
    // content, so they are assigned by slot and never imply step identity.
    const managed = sectionItems('how_it_works');
    const source = managed.length
      ? managed.map((b) => ({ title: loc(b.title, lang), body: loc(b.description, lang),
        icon: b.icon }))
      : fallback;
    return source.map((s, i) => ({
      // The icon is CMS content; the original four stay the fallback for their
      // own slot, and an unknown identifier degrades to the neutral default
      // instead of breaking the card.
      icon: resolveIcon(s.icon, icons[i % icons.length]),
      title: s.title || fallback[i % fallback.length].title,
      body: s.body || fallback[i % fallback.length].body,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, cms.features, lang]);

  // TREATX-V3-CMS-GAP-01 — Why TreatX points, managed as child records.
  const whyPoints = useMemo(() => {
    const icons = [Building2, MessageSquare, FileText, ShieldCheck];
    const fallback = [
      { title: t('v3.why.a.t', 'Published provider details'),
        body: t('v3.why.a.b', 'See the information a provider publishes about itself, including verified credentials.') },
      { title: t('v3.why.b.t', 'Direct conversation'),
        body: t('v3.why.b.b', 'Ask your questions yourself and keep the whole exchange in one thread.') },
      { title: t('v3.why.c.t', 'Written quotations'),
        body: t('v3.why.c.b', 'Receive an itemised offer you can read, keep and compare.') },
      { title: t('v3.why.d.t', 'Your details, your choice'),
        body: t('v3.why.d.b', 'You decide which provider receives your information and when.') },
    ];
    const managed = sectionItems('why');
    const source = managed.length
      ? managed.map((b) => ({ title: loc(b.title, lang), body: loc(b.description, lang),
        icon: b.icon }))
      : fallback;
    return source.map((s, i) => ({
      icon: resolveIcon(s.icon, icons[i % icons.length]),
      title: s.title || fallback[i % fallback.length].title,
      body: s.body || fallback[i % fallback.length].body,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, cms.features, lang]);

  // TREATX-V3-CMS-GAP-01 — hero trust bullets and Founding programme lines.
  const heroPoints = useMemo(() => {
    const fallback = [t('v3.hero.point1', 'No obligation'),
      t('v3.hero.point2', 'You choose who to contact'),
      t('v3.hero.point3', 'Written, itemised offers')];
    const managed = sectionItems('hero').map((b) => loc(b.title, lang)).filter(Boolean);
    return managed.length ? managed : fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, cms.features, lang]);

  const foundingLines = useMemo(() => {
    const fallback = [t('v3.founding.crm', 'CRM subscription — waived for the launch cohort'),
      t('v3.founding.psp', 'Payment-service charges — separate, always apply'),
      t('v3.founding.commission', 'Marketplace commercial terms — separate, always apply')];
    const managed = sectionItems('founding').map((b) => loc(b.title, lang)).filter(Boolean);
    return managed.length ? managed : fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, cms.features, lang]);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(selected ? `/find-clinics?treatment=${encodeURIComponent(selected)}` : '/find-clinics');
  };

  // TREATX-V3-SECTION-ORDER-01 — the sections below are rendered in the order
  // the owner saved (ascending `display_order`, deterministic tie-break), not
  // in the order they happen to be written here. The hero stays anchored above.
  const orderedSections = [
    ['how_it_works', () => (
      <Section testid="v3-how-it-works" className="v3-section py-10 sm:py-14">
        <Eyebrow>{sectionField('how_it_works', 'eyebrow', t('v3.how.eyebrow', 'How TreatX works'))}</Eyebrow>
        <H2>{sectionTitle('how_it_works', t('v3.how.title', 'Four steps, entirely at your pace'))}</H2>
        {sectionBody('how_it_works', '') && <Lede>{sectionBody('how_it_works', '')}</Lede>}
        <div className={`mt-8 grid gap-6 sm:grid-cols-2 ${gridColumns(steps.length)}`}>
          {steps.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="rounded-2xl border border-slate-200 p-5 transition-shadow hover:shadow-md"
                 data-testid={`v3-step-${i + 1}`}>
              <Icon className="h-5 w-5 text-teal-700" />
              <p className="mt-4 text-sm font-semibold text-slate-900">{title}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </div>
      </Section>
    )],
    ['treatments', () => (
      <Section testid="v3-treatments" className="v3-section bg-slate-50 py-10 sm:py-14">
        <Eyebrow>{sectionField('treatments', 'eyebrow', t('v3.treatments.eyebrow', 'Explore treatments'))}</Eyebrow>
        <H2>{sectionTitle('treatments', t('v3.treatments.title', 'Available through providers on TreatX'))}</H2>
        <Lede>{sectionBody('treatments', t('v3.treatments.body', 'Every treatment listed here is offered by at least one eligible provider.'))}</Lede>
        <div className="mt-8 flex flex-wrap gap-2">
          {treatments.slice(0, 18).map((x) => (
            <Link key={x.slug} to={`/find-clinics?treatment=${encodeURIComponent(x.slug)}`}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:border-teal-300 hover:text-teal-800"
                  data-testid={`v3-treatment-chip-${x.slug}`}>
              {x.name}
            </Link>
          ))}
        </div>
      </Section>
    )],
    ['providers', () => (
      <Section id="providers" testid="v3-providers" className="v3-section py-10 sm:py-14">
        <Eyebrow>{sectionField('providers', 'eyebrow', t('v3.providers.eyebrow', 'Explore healthcare providers'))}</Eyebrow>
        <H2>{sectionTitle('providers', t('v3.providers.title', 'See what each provider has published'))}</H2>
        <Lede>{sectionBody('providers', t('v3.providers.body', 'A sample of providers in the platform’s standard listing order, which takes account of the verification status and verified doctors a provider has on record. It is not a quality ranking, a recommendation or paid placement — and only credentials TreatX has verified are shown.'))}</Lede>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {providers.map((p) => (
            <Link key={p.clinic_id} to={`/clinics/${p.clinic_id}`}
                  className="group rounded-2xl border border-slate-200 p-5 transition-shadow hover:shadow-md"
                  data-testid={`v3-provider-card-${p.clinic_id}`}>
              <p className="text-sm font-semibold text-slate-900 group-hover:text-teal-800">
                {p.clinic_name}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                <MapPin className="h-3.5 w-3.5" />
                {[p.location?.city, p.location?.country].filter(Boolean).join(', ')}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(p.accreditations || []).filter((a) => a?.is_verified).slice(0, 3).map((a) => (
                  <span key={a.slug}
                        className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-1 text-[11px] font-medium text-teal-800">
                    <ShieldCheck className="h-3 w-3" /> {a.short_label}
                  </span>
                ))}
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-slate-700">
                {sectionField('providers', 'link_label', t('v3.providers.view', 'View provider'))} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
        <Link to={sectionHref('providers', 'cta_href', '/find-clinics')}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
              data-testid="v3-providers-all">
          {sectionField('providers', 'cta_label', t('v3.providers.all', 'See all providers'))} <ArrowRight className="h-4 w-4" />
        </Link>
      </Section>
    )],
    ['showcase', () => (
      <Section testid="v3-showcase" className="v3-section bg-slate-50 py-10 sm:py-14">
        <Eyebrow>{sectionField('showcase', 'eyebrow', t('v3.showcase.eyebrow', 'Inside TreatX'))}</Eyebrow>
        <H2>{sectionTitle('showcase', t('v3.showcase.title', 'What the experience looks like'))}</H2>
        <Lede>{sectionBody('showcase', t('v3.showcase.body', 'Discovery, provider comparison, direct messages and written quotations — all in one place.'))}</Lede>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(sceneVisuals('showcase').length
            ? sceneVisuals('showcase').map((b) => ({
              key: b.scene_key,
              caption: loc(b.title, lang),
              alt: loc(b.image_alt, lang) || loc(b.title, lang),
              image: b.compressed_url || b.image_url,
            }))
            : [t('v3.showcase.a', 'Treatment & provider discovery'),
              t('v3.showcase.b', 'Direct messages with a provider'),
              t('v3.showcase.c', 'Your personalised written offer')]
              .map((caption, i) => ({ key: `slot-${i + 1}`, caption, alt: caption, image: '' }))
          ).map(({ key, caption, alt, image }, i) => (
            <figure key={key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                    data-testid={`v3-showcase-slot-${i + 1}`}
                    data-scene-key={key}>
              {image ? (
                <img src={image} alt={alt}
                     loading="lazy"
                     className="h-auto w-full bg-slate-100 object-contain"
                     data-testid={`v3-showcase-image-${i + 1}`} />
              ) : (
                <div className="grid aspect-[16/10] place-items-center bg-[repeating-linear-gradient(135deg,#f1f5f9_0px,#f1f5f9_12px,#e2e8f0_12px,#e2e8f0_24px)]">
                  <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-slate-500"
                        data-testid={`v3-showcase-pending-${i + 1}`}>
                    {sectionField('showcase', 'badge_label', t('v3.showcase.pending', 'Product image pending review'))}
                  </span>
                </div>
              )}
              <figcaption className="px-4 py-3 text-xs text-slate-600">{caption}</figcaption>
            </figure>
          ))}
        </div>
      </Section>
    )],
    ['why', () => (
      <Section testid="v3-why" className="v3-section py-10 sm:py-14">
        <Eyebrow>{sectionField('why', 'eyebrow', t('v3.why.eyebrow', 'Why TreatX'))}</Eyebrow>
        <H2>{sectionTitle('why', t('v3.why.title', 'Clarity before any commitment'))}</H2>
        {sectionBody('why', '') && <Lede>{sectionBody('why', '')}</Lede>}
        <div className={`mt-8 grid gap-6 sm:grid-cols-2 ${gridColumns(whyPoints.length)}`}>
          {whyPoints.map(({ icon: Icon, title, body }) => (
            <div key={title} data-testid="v3-why-item">
              <Icon className="h-5 w-5 text-teal-700" />
              <p className="mt-3 text-sm font-semibold text-slate-900">{title}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </div>
      </Section>
    )],
    ['certifications', () => (
      <div data-testid="v3-certifications">
        <CorporateCertificates certificates={certificates} variant="homepage" requireEvidence />
      </div>
    )],
    ['provider_cta', () => (
      <Section testid="v3-for-providers" className="bg-[#07202a] py-16 text-white sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
          <div>
            <Eyebrow><span className="text-teal-300">{sectionField('provider_cta', 'eyebrow', t('v3.provider.eyebrow', 'For healthcare providers'))}</span></Eyebrow>
            <h2 className="v3-h2 mt-2 text-xl font-semibold tracking-tight md:text-2xl">
              {/* Single authority: Provider Benefits owns this headline, body and
                  first button label. The V3 editor deliberately does NOT offer
                  them, so there is nothing here that could look editable but be
                  overridden. */}
              {loc(providerContent.hero?.headline, lang)
                || t('v3.provider.title', 'Your patients. Your team. Your workflow. One connected platform.')}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">
              {loc(providerContent.hero?.body_md, lang)
                || t('v3.provider.body', 'Bring your own customers, work your leads with your sales team, prepare quotations and follow patients through treatment — alongside the patients who find you on TreatX.')}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={sectionHref('provider_cta', 'cta_href', '/provider/register')}
                    className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-5 py-2.5 text-sm font-semibold text-[#07202a] hover:bg-teal-400"
                    data-testid="v3-provider-join">
                {loc(providerContent.cta?.cta_text, lang)
                  || t('v3.provider.join', 'Join TreatX as a provider')}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={sectionHref('provider_cta', 'cta_href_secondary', '/provider-benefits')}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/30 hover:bg-white/10"
                    data-testid="v3-provider-benefits">
                {sectionField('provider_cta', 'cta_label_secondary', t('v3.provider.explore', 'Explore provider benefits'))}
              </Link>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {providerFeatureCards.map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10"
                   data-testid="v3-provider-feature">
                <Icon className="h-4 w-4 text-teal-300" />
                <p className="mt-3 text-sm text-slate-100">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Founding Partner — commercial claims stay separated and undated.
            Gated by the canonical surface rule (`campaigns`), which is
            permissive on GLOBAL and consults the TR matrix; it fails closed
            until the surface has resolved. */}
        {campaignsAllowed === true && sectionVisible('founding') && (
          <div className="mt-10 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10"
               data-testid="v3-founding-partner">
            <p className="text-sm font-semibold text-teal-300">
              {sectionTitle('founding', t('v3.founding.title', 'Founding provider programme'))}
            </p>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300">
              {sectionBody('founding', t('v3.founding.body', 'Providers joining during the launch cohort use the TreatX CRM without a subscription charge. Payment-service charges and marketplace commercial terms are separate and always apply.'))}
            </p>
            <ul className="mt-4 grid gap-2 text-xs text-slate-400 sm:grid-cols-3">
              {foundingLines.map((line, i) => (
                <li key={line} data-testid={`v3-founding-line-${i + 1}`}>{line}</li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-slate-500" data-testid="v3-founding-terms">
              {sectionField('founding', 'footnote', t('v3.founding.terms', 'Exact terms are confirmed in your provider agreement.'))}
            </p>
          </div>
        )}
      </Section>
    )],
    ['academy', () => (
      <Section id="academy" testid="v3-academy" className="v3-section py-10 sm:py-14">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-2xl">
              <Eyebrow>{sectionField('academy', 'eyebrow', t('v3.academy.eyebrow', 'TreatX Academy'))}</Eyebrow>
              <H2>{loc(featureBlocks('academy')[0]?.title, lang)
                || sectionTitle('academy', t('v3.academy.title', 'Professional training for clinic teams'))}</H2>
              <Lede>{loc(featureBlocks('academy')[0]?.description, lang)
                || sectionBody('academy', t('v3.academy.body', 'Practical, in-person training for the people who speak with international patients every day — enquiry handling, quotation practice and patient follow-up.'))}</Lede>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                {(sectionField('academy', 'badge_label')
                  || featureBlocks('academy')[0]?.button_text
                  || t('v3.academy.planned', 'Planned session details will be announced')) && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
                        data-testid="v3-academy-status">
                    <CalendarDays className="h-3.5 w-3.5 text-teal-700" />
                    {sectionField('academy', 'badge_label')
                      || featureBlocks('academy')[0]?.button_text
                      || t('v3.academy.planned', 'Planned session details will be announced')}
                  </span>
                )}
                <span className="text-xs text-slate-500" data-testid="v3-academy-registration-note">
                  {sectionField('academy', 'footnote', t('v3.academy.notOpen', 'Registration is not open yet.'))}
                </span>
              </div>
            </div>
            {featureBlocks('academy')[0]?.image_url ? (
              <img src={featureBlocks('academy')[0].thumbnail_url
                || featureBlocks('academy')[0].image_url}
                   alt={loc(featureBlocks('academy')[0]?.title, lang) || 'TreatX Academy'}
                   className="h-24 w-40 rounded-xl object-cover"
                   data-testid="v3-academy-image" />
            ) : (
              <GraduationCap className="h-10 w-10 text-teal-700" />
            )}
          </div>
        </div>
      </Section>
    )],
    ['apps', () => (
      <Section testid="v3-mobile-apps" className="pb-16 sm:pb-20">
        <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 p-6 sm:flex-row sm:items-center">
          <div className="flex items-start gap-3">
            <Smartphone className="mt-0.5 h-5 w-5 text-teal-700" />
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {sectionTitle('apps', t('v3.apps.title', 'TreatX for Android and iOS'))}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {sectionBody('apps', t('v3.apps.body', 'The mobile applications are in preparation. TreatX works in your mobile browser today.'))}
              </p>
            </div>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                data-testid="v3-apps-status">
            {sectionField('apps', 'badge_label', t('v3.apps.comingSoon', 'Coming soon'))}
          </span>
        </div>
      </Section>
    )],
    ['final_cta', () => (
      <Section testid="v3-final-cta" className="bg-teal-600 py-14 text-white sm:py-16">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <h2 className="v3-h2 text-xl font-semibold tracking-tight md:text-2xl">
              {sectionTitle('final_cta', t('v3.final.title', 'Start with a question, not a commitment.'))}
            </h2>
            <p className="mt-2 max-w-xl text-sm text-teal-50">
              {sectionBody('final_cta', t('v3.final.body', 'Find a provider, ask what you need to know and decide in your own time.'))}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={sectionHref('final_cta', 'cta_href', '/find-clinics')}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-teal-800 hover:bg-teal-50"
                  data-testid="v3-final-patient-cta">
              {sectionField('final_cta', 'cta_label', t('v3.final.patient', 'Find a provider'))} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={sectionHref('final_cta', 'cta_href_secondary', '/provider/register')}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/50 hover:bg-white/10"
                  data-testid="v3-final-provider-cta">
              {sectionField('final_cta', 'cta_label_secondary', t('v3.final.provider', 'Join as a provider'))}
            </Link>
          </div>
        </div>
      </Section>
    )],
  ]
    .filter(([key]) => sectionVisible(key))
    .map(([key, render]) => ({ key, render, storedOrder: sectionCopy(key)?.display_order }))
    .sort(compareSections);

  const homepageSeo = useHomepageSeoProps({ draftPreview });

  return (
    <div className="v3-root min-h-screen bg-white text-slate-900" data-testid="landing-v3">
      {/* ── Head metadata (TREATX-SEO-RECOVERY-S1) ───────────────────
          V3 carried NO metadata before this, so publishing it at `/` would
          have dropped the homepage title, description, canonical, social
          card and Organization JSON-LD. Values come from the one shared
          homepage authority (`pages/homepageSeo.js`) that V1 also uses, so
          publication changes the page and nothing about its head. `/v3`
          and the admin draft preview render noindex. */}
      <SEO {...homepageSeo} />
      {/* ── Resume journey (V1V3-PARITY-03) ──────────────────────────
          Rendered only for a signed-in patient or an unfinished flow. The
          destination comes from the canonical patient-flow step resolver, or
          the workspace dashboard resolver when there is no active flow. */}
      {showContinueBanner && (
        <div className="bg-[#07202a] px-5 py-2.5 text-white sm:px-8 lg:px-12"
             data-testid="v3-continue-journey-banner">
          <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-200">
              {hasActiveFlow ? (
                <>
                  <span className="font-semibold text-white">
                    {t('v3.resume.title', 'Continue your journey')}:
                  </span>{' '}
                  {flowState?.selectedTreatment ? `${flowState.selectedTreatment} • ` : ''}
                  {flowStepLabel()}
                </>
              ) : (
                <span className="font-semibold text-white">
                  {t('v3.resume.welcome', 'Welcome back. Your treatment journey awaits.')}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={() => navigate(
                hasActiveFlow
                  ? getNextStep()
                  : resolveDashboardRouteForActiveWorkspace(activeWorkspace, availableWorkspaces, activeWorkspaceId),
              )}
              className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-4 py-1.5 text-sm font-semibold text-[#07202a] transition-colors hover:bg-teal-400"
              data-testid="v3-continue-journey-btn"
            >
              {hasActiveFlow ? t('v3.resume.continue', 'Continue') : t('v3.resume.myDashboard', 'My Dashboard')}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Announcements (Landing CMS) ──────────────────────────────
          Publication status, scheduling, locale and GLOBAL/TR applicability
          are all decided by the CMS authority; a record reaches V3 only when a
          Super Admin has explicitly scoped it to this homepage version, so
          legacy V1/V2 marketing copy is never inherited and no record is
          modified here. Several valid announcements may show. */}
      {visibleAnnouncements.length > 0 && (
        <Section testid="v3-announcement" className="bg-teal-50/70 py-4">
          <div className="space-y-3">
            {visibleAnnouncements.map((a) => (
              <div key={a.key}
                   className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                   data-testid="v3-announcement-item">
                <div className="flex items-start gap-3">
                  <Share2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900"
                       data-testid="v3-announcement-title">{a.title}</p>
                    {a.description && (
                      <p className="mt-1 text-sm text-slate-600">{a.description}</p>
                    )}
                  </div>
                </div>
                {a.button_text && a.button_link && (
                  <Link to={a.button_link}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-medium text-teal-800 ring-1 ring-teal-200 hover:bg-teal-100"
                        data-testid="v3-announcement-cta">
                    {a.button_text} <ChevronRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Hero + canonical treatment discovery ────────────────────── */}
      <Section testid="v3-hero" className="bg-[#07202a] py-16 text-white sm:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Eyebrow>
              <span className="text-teal-300">
                {loc(cms.hero?.eyebrow, lang)
                  || t('v3.hero.eyebrow', 'International healthcare marketplace')}
              </span>
            </Eyebrow>
            <h1 className="v3-h1 mt-3 text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
              {loc(cms.hero?.title, lang)
                || t('v3.hero.title', 'Find the right healthcare provider — and ask them yourself.')}
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">
              {loc(cms.hero?.description, lang)
                || t('v3.hero.body', 'Discover treatments that providers genuinely offer, compare what each one publishes, message them directly and receive a written quotation prepared for your case.')}
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
              {heroPoints.map((p) => (
                <li key={p} className="flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-teal-300" /> {p}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={onSearch}
                className="rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5 sm:p-6"
                data-testid="v3-hero-search">
            <label htmlFor="v3-treatment" className="text-sm font-semibold text-slate-900">
              {sectionTitle('hero_search', t('v3.hero.searchTitle', 'What are you looking for?'))}
            </label>
            <p className="mt-1 text-xs text-slate-500">
              {sectionBody('hero_search', t('v3.hero.searchHint', 'Only treatments currently offered by providers on TreatX.'))}
            </p>
            <select id="v3-treatment" value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-100"
                    data-testid="v3-treatment-select">
              <option value="">{sectionField('hero_search', 'link_label', t('v3.hero.anyTreatment', 'Any treatment'))}</option>
              {treatments.map((x) => (
                <option key={x.slug} value={x.slug}>{x.name}</option>
              ))}
            </select>
            <button type="submit"
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700"
                    data-testid="v3-search-submit">
              <Search className="h-4 w-4" />
              {sectionField('hero_search', 'cta_label', t('v3.hero.cta', 'Explore providers'))}
            </button>
            <p className="mt-3 text-center text-xs text-slate-500">
              {sectionField('hero_search', 'footnote', t('v3.hero.noAccount', 'Browsing needs no account.'))}
            </p>
          </form>
        </div>

        {/* CMS-managed product visual. It sits BELOW the search panel so the
            canonical treatment discovery is never displaced, and it is shown
            only when an approved variant exists for the resolved surface. */}
        {heroVisual && (
          <figure className="mt-10 overflow-hidden rounded-2xl bg-white/5 ring-1 ring-white/10"
                  data-testid="v3-hero-visual">
            <img src={heroVisual.compressed_url || heroVisual.image_url}
                 alt={loc(heroVisual.image_alt, lang) || loc(heroVisual.title, lang)
                   || t('v3.hero.visualAlt', 'The TreatX application')}
                 loading="lazy"
                 className="mx-auto h-auto max-h-[420px] w-full object-contain"
                 data-testid="v3-hero-visual-image" />
            {loc(heroVisual.title, lang) && (
              <figcaption className="px-4 py-3 text-xs text-slate-300">
                {loc(heroVisual.title, lang)}
              </figcaption>
            )}
          </figure>
        )}
      </Section>

      {/* ── How TreatX works ────────────────────────────────────────── */}
      {orderedSections.map(({ key, render }) => (
        <React.Fragment key={key}>{render()}</React.Fragment>
      ))}

      {/* ── Explore treatments (provider-eligible only) ─────────────── */}

      {/* ── Explore providers ────────────────────────────────────────
          Wording check (owner request §9): the canonical read model
          `/search/providers` is NOT neutrally ordered — it sorts by
          verification status, verified-doctor signals and doctor counts. The
          copy therefore states exactly that and claims no neutrality, no
          quality ranking and no paid placement. Only credentials the
          authoritative record marks `is_verified` are shown. */}

      {/* ── Authentic product experience ─────────────────────────────
          Each scene is a PAIR of CMS records sharing a `scene_key`: an English
          visual scoped to GLOBAL and a Turkish visual scoped to TR. The server
          serves only the approved variant whose embedded language matches the
          canonically resolved surface, so a TR screen is never substituted on
          GLOBAL (or the reverse). Images are shown whole (`object-contain`) —
          application interfaces must not be cropped — and a scene without an
          approved visual keeps an explicit pending slot instead of a stand-in. */}

      {/* ── Why TreatX (factual only) ───────────────────────────────── */}

      {/* ── Certifications (canonical SSOT component) ───────────────── */}

      {/* ── For providers (existing site-content authority) ─────────── */}

      {/* ── TreatX Academy ───────────────────────────────────────────
          Presentation only — no registration endpoint, no training records
          and no operational Academy module. Title, description, planned
          session details, imagery and the registration message are all
          CMS-managed (`feature_content`, section "academy"); the built-in copy
          below is only a draft default and states no fixed commitment. */}

      {/* ── Mobile applications (accurate Coming Soon, CMS-managed) ─── */}

      {/* ── Final CTA ───────────────────────────────────────────────── */}

      {/* The application shell already renders the global <Footer />, which
          carries the legal CMS links (V1 proves it: five legal surfaces with
          no page-level footer). Rendering <LegalFooter /> here as well put a
          SECOND <footer> and a duplicate set of legal links on the page, so
          the page now relies on the shared footer alone. */}

      <p className="pb-6 text-center text-[11px] text-slate-400" data-testid="v3-surface-note">
        {t('v3.surfaceNote', 'Preview — surface')}: {surface}
      </p>
    </div>
  );
};

export default LandingPageV3;
